import type { FieldMissionItem } from '@groupe-reca/contracts'
import type { DetectionSnapshot } from '@/features/detection/application/DetectionRunner'
import { metersBetween, type LatLng } from '@/features/detection/domain/geometry'
import type { EngineEvent } from '@/features/detection/domain/types'
import { byRouteOrder } from './residenceOrder'

// Résidence mise en avant dans le panneau gauche (maquettes « Écran Mission ») : la résidence active du moteur,
// sinon une candidate en approche, sinon la prochaine à faire dans l'ordre choisi (`ordered`, par défaut la route).

export type FocusTone = 'next' | 'approach' | 'capture' | 'working' | 'suspended' | 'problem'

export interface Focus {
  item: FieldMissionItem
  tone: FocusTone
  /** Temps observé par le moteur pour la visite active. */
  observedMs: number | null
  distanceM: number | null
}

/** Durée pendant laquelle une capture récente s'affiche « drapeau capturé » avant « en cours ». */
const captureDisplayMs = 10_000

export const focusLabels: Record<FocusTone, string> = {
  next: 'Prochaine résidence',
  approach: 'En approche · à confirmer',
  capture: 'Drapeau capturé · adresse verrouillée',
  working: 'En cours',
  suspended: 'En cours · détection suspendue',
  problem: 'Problème',
}

function distanceTo(
  item: FieldMissionItem,
  flags: Record<string, LatLng | null> | null,
  snapshot: DetectionSnapshot | null,
): number | null {
  const flag = flags?.[item.id] ?? null
  const fix = snapshot?.lastFix
  return flag && fix ? Math.round(metersBetween(flag, fix.point)) : null
}

export function focusOf(
  items: readonly FieldMissionItem[],
  snapshot: DetectionSnapshot | null,
  flags: Record<string, LatLng | null> | null,
  problemFocusId: string | null,
  ordered: readonly FieldMissionItem[] = byRouteOrder(items),
): Focus | null {
  const byId = (itemId: string) => items.find((item) => item.id === itemId)
  const active = snapshot?.view.active
  if (active) {
    const item = byId(active.itemId)
    if (item?.status === 'WAITING') {
      const suspended = snapshot.view.gps.status === 'SUSPENDED'
      return {
        item,
        tone: suspended ? 'suspended' : active.observedMs < captureDisplayMs ? 'capture' : 'working',
        observedMs: active.observedMs,
        distanceM: null,
      }
    }
  }
  if (problemFocusId) {
    const item = byId(problemFocusId)
    if (item?.status === 'PROBLEM') return { item, tone: 'problem', observedMs: null, distanceM: null }
  }
  const approaching = ordered.find(
    (item) => item.status === 'WAITING' && snapshot?.view.statuses[item.id] === 'APPROACH',
  )
  if (approaching) {
    return { item: approaching, tone: 'approach', observedMs: null, distanceM: distanceTo(approaching, flags, snapshot) }
  }
  const next = ordered.find((item) => item.status === 'WAITING')
  return next ? { item: next, tone: 'next', observedMs: null, distanceM: distanceTo(next, flags, snapshot) } : null
}

/** Résidence suivante dans l'ordre choisi après celle mise en avant (pied du panneau « focus »). */
export function nextAfter(
  items: readonly FieldMissionItem[],
  focusId: string | null,
  ordered: readonly FieldMissionItem[] = byRouteOrder(items),
): FieldMissionItem | null {
  return ordered.find((item) => item.status === 'WAITING' && item.id !== focusId) ?? null
}

export type Decision =
  | Extract<EngineEvent, { kind: 'COMPLETED'; source: 'AUTO' }>
  | Extract<EngineEvent, { kind: 'VISIT_NOT_ADMISSIBLE' }>

export function decisionKey(event: Decision): string {
  return `${event.kind}:${event.itemId}:${String(event.at)}`
}

/** Dernière fin de visite proposée par le moteur, si aucune capture ne l'a suivie. */
export function latestDecision(snapshot: DetectionSnapshot | null): Decision | null {
  const decisions = snapshot?.decisions ?? []
  for (let index = decisions.length - 1; index >= 0; index -= 1) {
    const event = decisions[index]
    if (!event) continue
    if (event.kind === 'CAPTURED' || (event.kind === 'COMPLETED' && event.source === 'MANUAL')) return null
    if (event.kind === 'VISIT_NOT_ADMISSIBLE' || event.kind === 'COMPLETED') return event
  }
  return null
}
